import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { MapPin, Plus, Clock, CheckCircle, Navigation, User, DollarSign } from 'lucide-react';
import RouteMap from '../components/RouteMap';

import API_URL from '../utils/api';

const Visits = () => {
    const { user, canManage } = useAuth();
    const [visits, setVisits] = useState([]);
    const [todayVisits, setTodayVisits] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [formData, setFormData] = useState({
        customer_name: '', customer_address: '', customer_phone: '', visit_purpose: '', notes: '', planned_date: ''
    });

    useEffect(() => {
        fetchData();
        getCurrentLocation();
    }, []);

    const fetchData = async () => {
        try {
            const requests = [
                axios.get(`${API_URL}/api/visits`, { withCredentials: true }),
                axios.get(`${API_URL}/api/visits/today`, { withCredentials: true })
            ];
            if (canManage) requests.push(axios.get(`${API_URL}/api/employees`, { withCredentials: true }));
            const results = await Promise.all(requests);
            setVisits(results[0].data);
            setTodayVisits(results[1].data);
            if (results[2]) setEmployees(results[2].data.filter(e => e.role !== 'admin'));
        } catch (error) {
            logger.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => setCurrentLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
                (err) => logger.error('Location error:', err),
                { enableHighAccuracy: true }
            );
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!currentLocation) {
            alert('Please enable location access');
            return;
        }
        try {
            await axios.post(`${API_URL}/api/visits`, {
                ...formData,
                latitude: currentLocation.latitude,
                longitude: currentLocation.longitude,
                planned_date: formData.planned_date ? new Date(formData.planned_date).toISOString() : new Date().toISOString()
            }, { withCredentials: true });
            setIsDialogOpen(false);
            setFormData({ customer_name: '', customer_address: '', customer_phone: '', visit_purpose: '', notes: '', planned_date: '' });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error creating visit');
        }
    };

    const lookupAddress = async () => {
        if (!currentLocation) { alert('Enable location first'); return; }
        try {
            const { data } = await axios.get(`${API_URL}/api/geocode/reverse?lat=${currentLocation.latitude}&lng=${currentLocation.longitude}`, { withCredentials: true });
            if (data.address) setFormData(f => ({...f, customer_address: data.address}));
        } catch (error) { logger.error('Address lookup failed:', error); }
    };

    const handleCheckIn = async (visitId) => {
        if (!currentLocation) {
            alert('Please enable location access');
            getCurrentLocation();
            return;
        }
        try {
            await axios.post(`${API_URL}/api/visits/${visitId}/check-in`, {
                visit_id: visitId,
                latitude: currentLocation.latitude,
                longitude: currentLocation.longitude
            }, { withCredentials: true });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error checking in');
        }
    };

    const handleCheckOut = async (visitId) => {
        if (!currentLocation) {
            alert('Please enable location access');
            return;
        }
        const outcome = prompt('Enter visit outcome:');
        if (!outcome) return;
        
        try {
            await axios.post(`${API_URL}/api/visits/${visitId}/check-out`, {
                visit_id: visitId,
                latitude: currentLocation.latitude,
                longitude: currentLocation.longitude,
                outcome: outcome
            }, { withCredentials: true });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error checking out');
        }
    };

    const getStatusColor = (status) => {
        const colors = { planned: 'bg-slate-100 text-slate-800', in_progress: 'bg-blue-100 text-blue-800', completed: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' };
        return colors[status] || colors.planned;
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading visits...</div></div>;

    return (
        <div className="space-y-6" data-testid="visits-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Field Visits</h1>
                    <p className="text-slate-500 mt-1">Track customer visits and field activities</p>
                </div>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-brand hover:bg-brand rounded-none" data-testid="plan-visit-btn"><Plus className="h-4 w-4 mr-2" />Plan Visit</Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none">
                        <DialogHeader><DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Plan Customer Visit</DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Plan a customer visit — assign field staff, schedule and notes.</DialogDescription></DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Customer Name</Label>
                                <Input value={formData.customer_name} onChange={(e) => setFormData({...formData, customer_name: e.target.value})} className="rounded-none mt-1" required />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Customer Address</Label>
                                <div className="flex gap-2 mt-1">
                                    <Input value={formData.customer_address} onChange={(e) => setFormData({...formData, customer_address: e.target.value})} className="rounded-none flex-1" required />
                                    <Button type="button" variant="outline" size="sm" className="rounded-none text-xs" onClick={lookupAddress} data-testid="lookup-address-btn">
                                        <MapPin className="h-3 w-3 mr-1" />Auto
                                    </Button>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label>
                                    <Input value={formData.customer_phone} onChange={(e) => setFormData({...formData, customer_phone: e.target.value})} className="rounded-none mt-1" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Planned Date</Label>
                                    <Input type="datetime-local" value={formData.planned_date} onChange={(e) => setFormData({...formData, planned_date: e.target.value})} className="rounded-none mt-1" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Visit Purpose</Label>
                                <Input value={formData.visit_purpose} onChange={(e) => setFormData({...formData, visit_purpose: e.target.value})} className="rounded-none mt-1" required />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label>
                                <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="rounded-none mt-1" rows={2} />
                            </div>
                            {currentLocation && (
                                <p className="text-sm text-green-600 flex items-center gap-1"><Navigation className="h-4 w-4" /> Location captured</p>
                            )}
                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" className="rounded-none" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" className="bg-brand hover:bg-brand rounded-none">Plan Visit</Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {todayVisits.length > 0 && (
                <Card className="rounded-none border-slate-200 border-l-4 border-l-brand">
                    <CardHeader className="border-b border-slate-200">
                        <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <Clock className="h-5 w-5 text-brand" />Today's Visits ({todayVisits.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="divide-y divide-slate-200">
                            {todayVisits.map((visit) => (
                                <div key={visit._id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 bg-blue-100 rounded-full"><MapPin className="h-4 w-4 text-brand" /></div>
                                        <div>
                                            <p className="font-medium text-slate-900">{visit.customer_name}</p>
                                            <p className="text-sm text-slate-500">{visit.customer_address}</p>
                                            <p className="text-sm text-slate-600 mt-1">{visit.visit_purpose}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Badge className={`rounded-none ${getStatusColor(visit.status)}`}>{visit.status.replace('_', ' ')}</Badge>
                                        {visit.status === 'planned' && (
                                            <Button size="sm" className="rounded-none bg-green-600 hover:bg-green-700" onClick={() => handleCheckIn(visit._id)}>
                                                <MapPin className="h-4 w-4 mr-1" /> Check In
                                            </Button>
                                        )}
                                        {visit.status === 'in_progress' && (
                                            <Button size="sm" className="rounded-none bg-brand hover:bg-brand" onClick={() => handleCheckOut(visit._id)}>
                                                <CheckCircle className="h-4 w-4 mr-1" /> Check Out
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card className="rounded-none border-slate-200">
                <CardHeader className="border-b border-slate-200">
                    <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <MapPin className="h-5 w-5 text-brand" />All Visits
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50">
                                {canManage && <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Employee</th>}
                                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Customer</th>
                                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Purpose</th>
                                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Date</th>
                                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Duration</th>
                                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Outcome</th>
                                <th className="text-center p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visits.map((visit) => (
                                <tr key={visit._id} className="border-b border-slate-200 hover:bg-slate-50">
                                    {canManage && <td className="p-4 text-slate-900">{visit.employee_name}</td>}
                                    <td className="p-4">
                                        <p className="font-medium text-slate-900">{visit.customer_name}</p>
                                        <p className="text-sm text-slate-500">{visit.customer_address}</p>
                                    </td>
                                    <td className="p-4 text-slate-700">{visit.visit_purpose}</td>
                                    <td className="p-4 text-slate-700">{visit.planned_date ? new Date(visit.planned_date).toLocaleDateString() : '-'}</td>
                                    <td className="p-4 text-slate-700">{visit.duration_minutes ? `${Math.round(visit.duration_minutes)} min` : '-'}</td>
                                    <td className="p-4 text-slate-700 max-w-xs truncate">{visit.outcome || '-'}</td>
                                    <td className="p-4 text-center"><Badge className={`rounded-none ${getStatusColor(visit.status)}`}>{visit.status.replace('_', ' ')}</Badge></td>
                                </tr>
                            ))}
                            {visits.length === 0 && <tr><td colSpan={canManage ? 7 : 6} className="p-8 text-center text-slate-500">No visits found</td></tr>}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            {/* Route Tracking Map (Admin/HR only) */}
            {canManage && <RouteMap employees={employees} />}
        </div>
    );
};

export default Visits;
